import { Form, Input, Tooltip, Divider, Typography, Select, Button, Modal, Spin } from "antd";
import { CloseOutlined, SaveOutlined, EyeOutlined } from "@ant-design/icons";
import { GiStethoscope } from "react-icons/gi";
import { useState, useRef } from "react";
import dayjs from "dayjs";
import BaseService from "../../../../service/base.service";
import notify from "../../../../components/common/notificationHelper";
import Examinations from "../../../../models/examination";
import MedicalConsultationDetails from "../../../../models/medicalConsultation/medicalConsultationDetails";
import MedicalConsultationPreviewModal from "./MedicalConsultationPreviewModal"
import MedicalConsultationComparisonModal from "./MedicalConsultationComparisonModal";
import { DiffOutlined } from "@ant-design/icons";
import { useEffect } from "react";
import { useIsMobile } from "../../../../hooks/useIsMobile";

const { Title } = Typography;
const { Option } = Select;
const StethoscopeIcon = GiStethoscope as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

const initialAnalyses = [
    "AZOT KREAT",
    "BILURUBINA TOTALE INDIREKTE DIREKTE",
    "AST/ALT",
    "ERITROSEDIMENT",
    "FIBRINOGJEN PCR",
    "VIT D 25 OH D",
    "TSH FT3 FT4",
    "TORCH - TOXO IGM IGG",
    "RUBELLA IGM IGG",
    "CMV IGM IGG",
    "HSV",
    "SIFILIZ QUICK TEST OSE IGM",
    "PARVOVIRUS B19 IGM IGG",
    "EBV IGM IGG",
    "VZV IGM IGG",
    "HEP B HBSAG",
    "HCV IGM",
    "HIV AG/AB COMBI",
    "ELEKTROFOREZA E HB",
    "APTT, INR",
    "FACTOR V LEIDEN",
    "KULTURE VAGINALE (GRAM MYK UREAPLASMA MYCOPLMA",
    "AF-GENITAL +KLAMIDIA",
    "SST",
    "HPV",
    "PAP TEST",
    "EKO OBSTETRIKALE +NT (TRANSLUCENCA NUKALE)",
    "EKO MORFOLOGJIKE MINOR JAVA 11-14",
    "BI TEST OSE NIPT",
    "EKO OBSTETRIKALE JAVA 11-14 ME MJEK TE HUAJ",
    "FIBROZA CISTIKE",
    "DUCHENNE &BECKER MUSKULAR DYSTROPHIES",
    "EGZAMINIME TE TJERA",
];

export interface ExaminationFormValues {
    id?: number;
    profession?: string;
    complaintVitae?: string;
    complaintPharmacological?: string;
    complaintFamily?: string;
    complaintSocial?: string;
    smokeUse?: string;
    alcoholUse?: string;
    substanceUse?: string;
    lifestyle?: string;
    diet?: string;
    physicalExamination?: string;
    height?: number;
    weight?: number;
    bmi?: number;
    diagnosis?: string;
    recommendations?: string[];
}

interface Props {
    isEdit: boolean;
    isModalOpen: boolean;
    onCancel: () => void;
    patientId: string;
    medicalConsultationId?: number | null;
    onSuccess: () => void;
}

const MedicalConsultationModal = ({
    isEdit,
    isModalOpen: open,
    onCancel,
    patientId,
    medicalConsultationId: editId,
    onSuccess,
}: Props) => {
    const isMobile = useIsMobile();
    const [analyses, setAnalyses] = useState(initialAnalyses);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm<ExaminationFormValues>();
    const horizontalPadding = isMobile ? 12 : 24;
    const contentRef = useRef<HTMLDivElement | null>(null);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewData, setPreviewData] = useState<ExaminationFormValues>({});
    const [compareVisible, setCompareVisible] = useState(false);

    const handleEdit = async (examiantion: Examinations) => {
        setLoading(true);
        try {
            const rp = await BaseService.get<MedicalConsultationDetails>("/medical-consultations/details/", examiantion.id);
            if (rp.Status) {
                const data = rp.Data;
                form.setFieldsValue({
                    ...data
                });
            } else {
                console.error("Stats error:", rp.Messages, rp.Exception);
            }
        } finally {
            setLoading(false);

        }
    };

    useEffect(() => {
        if (open) {
            requestAnimationFrame(() => {
                setTimeout(() => {
                    if (contentRef.current) {
                        contentRef.current.scrollTop = 0;
                    } else {
                        const modalBody = document.querySelector(".ant-modal-body") as HTMLElement | null;
                        if (modalBody) modalBody.scrollTop = 0;
                    }
                }, 0);
            });
        }
    }, [open]);

    useEffect(() => {
        if (open && isEdit && editId) {
            // 🔹 Fetch data when modal opens for editing
            handleEdit({ id: editId } as Examinations);
        } else if (open && !isEdit) {
            // 🔹 Reset form when adding a new examination
            form.resetFields();
        }
    }, [open, isEdit, editId]);

    const handleChange = (selectedValues: string[]) => {
        const remainingOptions = initialAnalyses.filter(
            (item) => !selectedValues.includes(item)
        );
        setAnalyses(remainingOptions);
    };

    const onValuesChange = (_changedValues: Partial<ExaminationFormValues>, allValues: ExaminationFormValues) => {
        // Calculate BMI
        const { weight, height } = allValues;
        const newBmi = calculateBMI(weight, height);
        const currentBmi = form.getFieldValue("bmi");
        if (newBmi !== currentBmi && newBmi !== null) {
            form.setFieldsValue({ bmi: newBmi });
        }
    };

    const calculateBMI = (weight?: number, height?: number) => {
        if (weight && height) {
            const heightInMeters = height / 100;
            if (heightInMeters === 0) return null;
            return parseFloat((weight / (heightInMeters * heightInMeters)).toFixed(1));
        }
        return null;
    };

    const openCompare = async () => setCompareVisible(true);

    const openPreview = async () => {
        try {
            const values = await form.validateFields();
            setPreviewData(values); setPreviewVisible(true);
        }
        catch {
            const allValues = form.getFieldsValue(true);
            setPreviewData(allValues);
            setPreviewVisible(true);
        }
    };
    const handleSubmit = async (values: ExaminationFormValues) => {
        try {
            let rp;
            if (isEdit && values.id) {
                rp = await BaseService.update("/medical-consultations/", values.id, values);
            } else {
                rp = await BaseService.create(`/medical-consultations/${patientId}`, values);
            }
            if (rp.Status) {
                notify({
                    title: "Success",
                    description: isEdit
                        ? "Examination has been updated."
                        : "Examination has been added.",
                    variant: "success",
                });

                onCancel(); // close modal
                onSuccess(); // 🔹 trigger refresh in parent
            } else {
                notify({
                    title: "Error",
                    description: "Failed to save Examination.",
                    variant: "error",
                });
                console.error("Messages:", rp.Messages, "Exception:", rp.Exception);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open) {
            setAnalyses(initialAnalyses);
        }
    }, [open]);

    return (
        <div>
            <Modal
                title={
                    <div
                        style={{
                            textAlign: "center",
                            fontWeight: 600,
                            fontSize: "1.5rem",
                            color: "#1f1f1f",
                            borderBottom: "1px solid #f0f0f0",
                            paddingBottom: 12,
                        }}
                    >
                        {isEdit ? (
                            <>
                                <StethoscopeIcon style={{ color: "#1677ff", marginRight: 8 }} />
                                Edit Medical Consultation
                            </>
                        ) : (
                            <>
                                <StethoscopeIcon style={{ color: "#1677ff", marginRight: 8 }} />
                                Add Medical Consultation
                            </>
                        )}
                        <div
                            style={{
                                fontSize: 14,
                                fontWeight: 400,
                                color: "#6b7280",
                                marginTop: 4,
                            }}
                        >
                            {isEdit
                                ? "Update the medical consultation details below"
                                : "Fill in the details to add a new medical consultation"}
                        </div>
                    </div>
                }
                centered
                open={open}
                footer={null}
                width={isMobile ? "calc(100vw - 24px)" : "60vw"}
                confirmLoading={loading}
                onCancel={onCancel}
                styles={{
                    body: { maxHeight: isMobile ? "75vh" : "80vh", overflowY: "auto", position: "relative" },
                }}
            >
                {loading && (
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            background: "rgba(255,255,255,0.7)",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            zIndex: 1000,
                        }}
                    >
                        <Spin size="large" />
                    </div>
                )}
                <div style={{ maxHeight: '65vh', overflowY: 'auto', paddingRight: 8 }}>
                    <Form <ExaminationFormValues>
                        form={form}
                        name="validateOnly"
                        layout="vertical"
                        autoComplete="off"
                        onFinish={handleSubmit}
                        initialValues={{
                            examinationDate: dayjs(),
                        }}
                        disabled={loading}
                        onValuesChange={onValuesChange}
                        style={{
                            padding: isEdit
                                ? `16px ${horizontalPadding}px 0`
                                : `0px ${horizontalPadding}px 0`,
                        }}
                    >
                        {/* Pregnancy Section */}
                        {isEdit && (
                            <Form.Item
                                name="id"
                                label="Examination ID"
                                rules={[{ required: true }]}
                            >
                                <Input disabled />
                            </Form.Item>
                        )}
                        <Title level={4}>Lifestyle & Habits</Title>
                        <Form.Item label="Profession" name="profession">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Smoking Status" name="smokeUse">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Alcohol Use" name="alcoholUse">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Substance Use" name="substanceUse">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Lifestyle" name="lifestyle">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Diet" name="diet">
                            <Input />
                        </Form.Item>
                        <Divider />

                        <Title level={4}>Complaints</Title>
                        <Form.Item label="A. Vitae" name="complaintVitae">
                            <Input />
                        </Form.Item>
                        <Form.Item label="A. Pharmacological" name="complaintPharmacological">
                            <Input />
                        </Form.Item>
                        <Form.Item label="B. Family" name="complaintFamily">
                            <Input />
                        </Form.Item>
                        <Form.Item label="A. Social" name="complaintSocial">
                            <Input />
                        </Form.Item>
                        <Divider />

                        <Title level={4}>Physical Measurements</Title>
                        <Form.Item label="Height (cm)" name="height">
                            <Input type="number" />
                        </Form.Item>
                        <Form.Item label="Weight (kg)" name="weight">
                            <Input type="number" />
                        </Form.Item>
                        <Form.Item label="BMI" name="bmi">
                            <Input type="number" />
                        </Form.Item>
                        <Divider />

                        <Title level={4}>Physical Examination</Title>
                        <Form.Item label="Physical Examination" name="physicalExamination">
                            <Input.TextArea rows={4} />
                        </Form.Item>
                        <Divider />

                        {/* Measurements Section */}
                        <Title level={4}>Diagnosis</Title>
                        <Form.Item label="Diagnosis" name="diagnosis">
                            <Input.TextArea rows={4} placeholder="Diagnosis" />
                        </Form.Item>

                        <Divider />

                        {/* Recommendation Section */}
                        <Title level={4}>Recommendation</Title>
                        <Form.Item label="Recommendation" name="recommendations">
                            <Select
                                mode="tags"
                                style={{ width: "100%" }}
                                placeholder="Select or type recommendations"
                                onChange={handleChange}
                                tokenSeparators={[',']}
                            >
                                {analyses.map((analysis) => (
                                    <Option key={analysis} value={analysis}>
                                        {analysis}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Form>
                </div>
                <div
                    style={{
                        position: "sticky",
                        bottom: 0,
                        background: "#fff",
                        padding: "20px 28px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 14,
                        borderTop: "1px solid #f0f0f0",
                        boxShadow: "0 -4px 12px rgba(0, 0, 0, 0.06)",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 16,
                        }}
                    >
                        <div style={{ display: "flex", gap: 10 }}>
                            <Tooltip title="Compare with previous examinations">
                                <Button icon={<DiffOutlined />} onClick={openCompare}>
                                    Compare
                                </Button>
                            </Tooltip>
                            <Tooltip title="Preview the examination before saving">
                                <Button icon={<EyeOutlined />} onClick={openPreview}>
                                    Preview
                                </Button>
                            </Tooltip>
                        </div>
                        <div style={{ display: "flex", gap: 10 }}>
                            <Button danger icon={<CloseOutlined />} onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={() => form.submit()}
                                loading={loading}
                            >
                                {isEdit ? "Update" : "Save"}
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>
            {/* Preview Modal */}
            <MedicalConsultationPreviewModal
                open={previewVisible}
                isEdit={isEdit}
                onClose={() => setPreviewVisible(false)}
                previewData={previewData}
            />
            <MedicalConsultationComparisonModal
                open={compareVisible}
                onClose={() => setCompareVisible(false)}
                patientId={patientId}
                formValues={form.getFieldsValue(true)}
            />
        </div>
    );
};

export default MedicalConsultationModal;
